import { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ADMIN_EMAILS } from '../App.js';

/**
 * Custom hook to access authentication state
 * @returns {Object} Authentication state and methods
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Set up auth state listener
  useEffect(() => {
    const auth = getAuth();
    
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (authUser) {
          setUser(authUser);
          
          // Check if user is admin
          let adminStatus = false;
          
          // First check via email (faster)
          if (Array.isArray(ADMIN_EMAILS) && authUser.email && ADMIN_EMAILS.includes(authUser.email)) {
            adminStatus = true;
          } else {
            // If not in the email list, check via custom claims
            try {
              // Get the ID token result which includes custom claims
              const idTokenResult = await authUser.getIdTokenResult();
              
              // Check if the admin custom claim exists
              if (idTokenResult.claims.admin === true) {
                adminStatus = true;
              }
            } catch (claimsError) {
              console.error('Error checking admin claims:', claimsError);
            }
          }
          
          setIsAdmin(adminStatus);
        } else {
          setUser(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
      } finally {
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Get user profile data from Firestore
  const getUserProfile = async (userId) => {
    if (!userId) return null;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  };

  return {
    user,
    isAdmin,
    loading,
    getUserProfile
  };
};
